/**
 * ProtectedRoute —— 路由级访问守卫。
 *
 *   - 启动水合中（loading）→ 渲染居中 Spinner，避免闪过登录页。
 *   - 未登录（user 为 null）→ 重定向到 /login，并把当前位置塞进 state.from，登录后跳回。
 *     例外：allowAnonymousInShadow 且显式配置 REACT_APP_AUTH_ENFORCE=false → 放行匿名访问。
 *   - 传了 requiredPermission 且当前用户不具备 → 渲染 403 占位（不跳转，保留导航）。
 *     注意：requiredPermission 不受影子放行影响——治理等敏感页仍按权限把关。
 *
 * 安全默认：未配置或任意非 false 值都进入强制模式；兼容模式只能被显式开启。
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { WxbSpinner } from '../wxb-ui/Spinner/Spinner';
import { WxbEmpty } from '../wxb-ui/Empty/Empty';
import { useAuth } from '../../contexts/AuthContext';
import './ProtectedRoute.css';

/** 前端默认强制登录；仅显式配置 false 才允许本地兼容模式。 */
export const isAuthEnforced = (): boolean => process.env.REACT_APP_AUTH_ENFORCE !== 'false';

export interface ProtectedRouteProps {
  children: React.ReactNode;
  /** 需要的权限码（permission_code）；不传则只校验是否登录。 */
  requiredPermission?: string;
  /** 需要的平台控制面权限码；与 Site 权限互斥使用。 */
  requiredPlatformPermission?: string;
  /** 影子模式下允许匿名访问（用于包裹现有非敏感页面，避免未登录被整站重定向）。 */
  allowAnonymousInShadow?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
  requiredPlatformPermission,
  allowAnonymousInShadow = false,
}) => {
  const location = useLocation();
  const {
    user,
    loading,
    hasPermission,
    hasPlatformPermission,
  } = useAuth();

  if (loading) {
    return (
      <div className="wxb-protected-route-loading">
        <WxbSpinner tip="加载中…" />
      </div>
    );
  }

  if (!user) {
    // 影子模式 + 允许匿名：直接渲染（不重定向）。否则跳登录页。
    if (allowAnonymousInShadow && !isAuthEnforced()) {
      return <>{children}</>;
    }
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <div className="wxb-protected-route-empty">
        <WxbEmpty description={`无权限访问该页面（需要权限：${requiredPermission}）`} />
      </div>
    );
  }

  if (
    requiredPlatformPermission
    && !hasPlatformPermission(requiredPlatformPermission)
  ) {
    return (
      <div className="wxb-protected-route-empty">
        <WxbEmpty
          description={`无权限访问平台控制面（需要权限：${requiredPlatformPermission}）`}
        />
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
